import { Plus, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { Disposition } from '@intone/shared/types'

interface DispositionsTableProps {
  dispositions: Disposition[]
  onDispositionsChange: (dispositions: Disposition[]) => void
}

const isSystemDisposition = (d: Disposition) =>
  d.isEditable === false && d.isDeletable === false

export function DispositionsTable({
  dispositions,
  onDispositionsChange,
}: DispositionsTableProps) {
  const visibleDispositions = dispositions.filter((d) => !isSystemDisposition(d))

  const addCustomDisposition = () => {
    const newDisposition: Disposition = {
      code: '',
      description: '',
      canCallback: true,
      isEditable: true,
      isDeletable: true,
    }
    onDispositionsChange([...dispositions, newDisposition])
  }

  const getFullIndex = (visibleIndex: number): number => {
    const visible = visibleDispositions[visibleIndex]
    return dispositions.indexOf(visible)
  }

  const removeCustomDisposition = (visibleIndex: number) => {
    const fullIndex = getFullIndex(visibleIndex)
    onDispositionsChange(
      dispositions.filter((d, i) => i !== fullIndex || d.isDeletable === false)
    )
  }

  const updateDisposition = (
    visibleIndex: number,
    field: 'code' | 'description',
    value: string
  ) => {
    const fullIndex = getFullIndex(visibleIndex)
    onDispositionsChange(
      dispositions.map((d, i) => {
        if (i !== fullIndex) return d
        if (field === 'code' && d.isDeletable === false) return d
        return { ...d, [field]: value }
      })
    )
  }

  const updateDispositionCallback = (visibleIndex: number, canCallback: boolean) => {
    const fullIndex = getFullIndex(visibleIndex)
    onDispositionsChange(
      dispositions.map((d, i) =>
        i === fullIndex && d.code !== 'DNC' ? { ...d, canCallback } : d
      )
    )
  }

  return (
    <Card>
      <CardHeader className="border-b py-2">
        <CardTitle className="text-body">Dispositions</CardTitle>
      </CardHeader>
      <CardContent className="pt-2 pb-3">
        <div className="space-y-3">
          <div className="overflow-x-auto">
            <Table>
              <colgroup>
                <col style={{ width: '120px' }} />
                <col />
                <col style={{ width: '70px' }} />
                <col style={{ width: '60px' }} />
              </colgroup>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Callback</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleDispositions.map((disposition, index) => (
                  <TableRow
                    key={`${disposition.isDeletable === false ? 'fixed' : 'custom'}-${index}`}
                  >
                    <TableCell className="py-1">
                      {disposition.isDeletable === false ? (
                        <code className="bg-muted px-2 py-1 rounded text-detail font-mono">
                          {disposition.code}
                        </code>
                      ) : (
                        <Input
                          value={disposition.code}
                          onChange={(e) =>
                            updateDisposition(index, 'code', e.target.value.toUpperCase())
                          }
                          placeholder="CODE"
                          className="h-7 font-mono w-full"
                        />
                      )}
                    </TableCell>
                    <TableCell className="py-1">
                      <Input
                        value={disposition.description}
                        onChange={(e) =>
                          updateDisposition(index, 'description', e.target.value)
                        }
                        placeholder="Enter disposition description..."
                        className="h-7 w-full"
                      />
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="flex justify-center">
                        <Checkbox
                          checked={disposition.canCallback}
                          onCheckedChange={(checked) =>
                            updateDispositionCallback(index, !!checked)
                          }
                          disabled={disposition.code === 'DNC'}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="flex justify-center">
                        {disposition.isDeletable === true && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCustomDisposition(index)}
                            className="h-6 w-6 p-0 text-gray-400 hover:text-destructive"
                          >
                            <Trash2 className="w-2.5 h-2.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={addCustomDisposition}
          >
            <Plus className="w-3 h-3" />
            Add Disposition
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
